<template>
  <div class="content">
    <h1 class="terms-title">Business terms &amp; conditions for Lulu Rewards</h1>
    <p><em>Effective date: 8 October 2026</em></p>

    <p class="intro-text">
      These Business Terms &amp; Conditions ("Terms") govern your use of the Lulu Rewards platform as a business partner.
      By completing the sign-up process and subscribing to Lulu Rewards, you agree to be bound by these Terms.
    </p>

    <ol>
      <li>
        <strong>{{ eligibilityHeading }}</strong>
        <ol type="1">
          <li>{{ eligibilityLine1 }}</li>
          <li>{{ eligibilityLine2 }}</li>
          <li>{{ eligibilityLine3 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ servicesHeading }}</strong>
        <ol type="1">
          <li>{{ servicesLine1 }}</li>
          <li>{{ servicesLine2 }}</li>
          <li>{{ servicesLine3 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ subscriptionHeading }}</strong>
        <ol type="1">
          <li>{{ subscriptionLine1 }}</li>
          <li>{{ subscriptionLine2 }}</li>
          <li>{{ subscriptionLine3 }}</li>
          <li>{{ subscriptionLine4 }}</li>
          <li>{{ subscriptionLine5 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ obligationsHeading }}</strong>
        <ol type="1">
          <li>{{ obligationsLine1 }}</li>
          <li>{{ obligationsLine2 }}</li>
          <li>{{ obligationsLine3 }}</li>
          <li>{{ obligationsLine4 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ ipHeading }}</strong>
        <ol type="1">
          <li>{{ ipLine1 }}</li>
          <li>{{ ipLine2 }}</li>
          <li>{{ ipLine3 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ dataHeading }}</strong>
        <ol type="1">
          <li>{{ dataLine1 }}</li>
          <li>{{ dataLine2 }}</li>
          <li>{{ dataLine3 }}</li>
          <li>{{ dataLine4 }}</li>
          <li>{{ dataLine5 }}</li>
          <li>{{ dataLine6 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ businessPrivacyHeading }}</strong>
        <ol type="1">
          <li>{{ businessPrivacyLine1 }}</li>
          <li>{{ businessPrivacyLine2 }}</li>
          <li>{{ businessPrivacyLine3 }}</li>
          <li>{{ businessPrivacyLine4 }}</li>
          <li>{{ businessPrivacyLine5 }}</li>
          <li>{{ businessPrivacyLine6 }}</li>
          <li>{{ businessPrivacyLine7 }}</li>
          <li>{{ businessPrivacyLine8 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ liabilityHeading }}</strong>
        <ol type="1">
          <li>{{ liabilityLine1 }}</li>
          <li>{{ liabilityLine2 }}</li>
          <li>{{ liabilityLine3 }}</li>
          <li>{{ liabilityLine4 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ terminationHeading }}</strong>
        <ol type="1">
          <li>{{ terminationLine1 }}</li>
          <li>{{ terminationLine2 }}</li>
          <li>{{ terminationLine3 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ governingLawHeading }}</strong>
        <ol type="1">
          <li>{{ governingLawLine1 }}</li>
          <li>{{ governingLawLine2 }}</li>
        </ol>
      </li>
      <li>
        <strong>{{ contactHeading }}</strong>
        <p v-html="contactLine"></p>
      </li>
    </ol>
  </div>
</template>

<script>
export default {
  name: 'BusinessTermsComponent',
  setup() {
    const eligibilityHeading = 'Eligibility and registration';
    const eligibilityLine1 = 'To use the Lulu Rewards platform as a business, you must be a legally registered business, sole trader, or other trading entity operating within the United Kingdom.';
    const eligibilityLine2 = 'The individual completing registration must have the authority to enter into legally binding agreements on behalf of the business.';
    const eligibilityLine3 = 'You must provide accurate and up-to-date information during registration and promptly notify us if any details change.';

    const servicesHeading = 'Services provided';
    const servicesLine1 = 'Lulu Rewards provides a digital loyalty and rewards card platform, enabling businesses to offer branded digital rewards cards to their customers via Apple Wallet and Google Wallet.';
    const servicesLine2 = 'The platform includes tools for configuring your rewards programme, operator scanning functionality (currently iOS only), and customer sign-up management.';
    const servicesLine3 = 'Lulu Rewards reserves the right to modify, update, or discontinue features of the platform at any time, with reasonable notice where practicable.';

    const subscriptionHeading = 'Subscription and payment';
    const subscriptionLine1 = 'Access to the Lulu Rewards platform requires an active subscription, billed either monthly or annually. Available plans are listed at sign-up and may be updated from time to time.';
    const subscriptionLine2 = 'Subscriptions are billed in advance, either monthly or annually depending on the billing period you choose at sign-up. All payments are processed securely by Stripe. By subscribing, you authorise Lulu Rewards to charge your payment method on a recurring basis.';
    const subscriptionLine3 = 'All subscription fees are non-refundable, including in the event of early cancellation.';
    const subscriptionLine4 = 'Lulu Rewards reserves the right to change subscription pricing with a minimum of 30 days\' written notice. Continued use of the platform following a pricing change constitutes acceptance of the new pricing.';
    const subscriptionLine5 = 'If a payment fails, Lulu Rewards may suspend access to the platform until the outstanding balance is settled.';

    const obligationsHeading = 'Business obligations';
    const obligationsLine1 = 'You are responsible for ensuring that all operators using the scanning application do so appropriately and in accordance with these Terms.';
    const obligationsLine2 = 'You must not manipulate, misuse, or attempt to circumvent the rewards system in any way that is deceptive or harmful to customers.';
    const obligationsLine3 = 'You are responsible for communicating your own rewards structure, redemption process, and any applicable limitations directly to your customers.';
    const obligationsLine4 = 'You must comply with all applicable laws and regulations, including consumer protection, advertising standards, and data protection legislation.';

    const ipHeading = 'Intellectual property';
    const ipLine1 = 'All software, platform design, algorithms, and branding belonging to Lulu Rewards remain the exclusive intellectual property of Lulu Rewards. Nothing in these Terms grants you any ownership of Lulu Rewards\' intellectual property.';
    const ipLine2 = 'You retain full ownership of your business name, logo, and other branding assets provided to Lulu Rewards during setup.';
    const ipLine3 = 'By uploading your branding assets to the platform, you grant Lulu Rewards a limited, non-exclusive, royalty-free licence to use and display your branding solely for the purpose of providing the service to you.';

    const dataHeading = 'Data protection (customer data processed on your behalf)';
    const dataLine1 = 'Lulu Rewards processes personal data of your customers on your behalf in its capacity as a data processor. You act as the data controller for customer data collected through the platform.';
    const dataLine2 = 'You are responsible for ensuring that you have a lawful basis for collecting and processing your customers\' data, including obtaining valid consent where required under UK GDPR.';
    const dataLine3 = 'Lulu Rewards will process customer data only in accordance with your instructions and our Customer Privacy Policy.';
    const dataLine4 = 'In the event of a data breach affecting your customers\' data, Lulu Rewards will notify you without undue delay and provide reasonable cooperation in any required regulatory notification.';
    const dataLine5 = 'Customer data is stored on Google Cloud (Firebase) in the European Union. Some processing, such as generating wallet passes and sending notifications, runs on Google Cloud services in the United States. Those transfers rely on the UK Extension to the EU-US Data Privacy Framework (the UK-US data bridge) and Google\'s data processing terms.';
    const dataLine6 = 'Where you enable marketing or review-request emails, Lulu Rewards sends them only to customers who have consented, on your behalf and as your processor. You are responsible for the content of those emails and for your lawful basis for sending them. Lulu Rewards includes an unsubscribe link in every marketing email and honours opt-outs on your behalf.';

    const businessPrivacyHeading = 'Business privacy notice (data we collect about you)';
    const businessPrivacyLine1 = 'This Business Privacy Notice explains how Lulu Rewards handles personal information about you as a business subscriber. It is separate from our processing of your customers\' data on your behalf, which is covered in the Data Protection clause above.';
    const businessPrivacyLine2 = 'Information we collect: business contact details (name, business or trading name, and email address; billing data (subscription plan, billing address, and payment metadata, with full card details held by our payment provider Stripe and not by us); account and usage data (login activity, operators added to your account, scanning activity, and platform configuration); and communication data (any correspondence you send us via email, phone, or in-app channels).';
    const businessPrivacyLine3 = 'Lawful bases: we process this information on the bases of performance of a contract (to provide the Lulu Rewards platform under your subscription), our legitimate interests (to operate, secure, and improve the service), compliance with our legal obligations, and your consent where required (for example, for marketing about Lulu Rewards).';
    const businessPrivacyLine4 = 'How we use it: to provide and operate the platform, to process subscription payments and renewals, to provide customer support, to detect and prevent fraud or misuse, to send you platform-related service notifications, and to send marketing about Lulu Rewards where you have given the relevant consent.';
    const businessPrivacyLine5 = 'Sharing: we share your information only with trusted service providers acting on our behalf - including Stripe (subscription billing), Mixpanel (analytics, which receives your email address together with your activity on the platform), and our cloud hosting and email providers - and with regulators or law enforcement authorities where required by applicable law, including in response to a court order or other valid legal request.';
    const businessPrivacyLine6 = 'Retention: we retain your information for as long as your subscription remains active and for a reasonable period thereafter to comply with our legal, accounting, and tax obligations. Where retention is no longer necessary, your information is securely deleted.';
    const businessPrivacyLine7 = 'Your rights: under the UK GDPR and the Data Protection Act 2018, you have rights of access, rectification, erasure, restriction, data portability, and objection in respect of your personal data. You can exercise these rights by contacting us at hello@lulurewards.co.uk.';
    const businessPrivacyLine8 = 'You also have the right to lodge a complaint with the Information Commissioner\'s Office (ICO) at ico.org.uk if you believe we have not handled your personal information in accordance with applicable UK data protection law.';

    const liabilityHeading = 'Liability and disclaimers';
    const liabilityLine1 = 'The Lulu Rewards platform is provided on an "as is" basis. We do not guarantee that the platform will be available at all times, error-free, or free from interruption.';
    const liabilityLine2 = 'Subject to the following paragraph, Lulu Rewards shall not be liable for any indirect, incidental, special, or consequential losses arising from your use of the platform, including loss of revenue, loss of data, or reputational damage.';
    const liabilityLine3 = 'Subject to the following paragraph, our total aggregate liability to you in connection with these Terms shall not exceed the total subscription fees paid by you in the three months preceding the event giving rise to the claim.';
    const liabilityLine4 = 'Nothing in these Terms shall exclude or limit our liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for any other liability that cannot lawfully be excluded or limited under applicable UK law.';

    const terminationHeading = 'Termination';
    const terminationLine1 = 'You may cancel your subscription at any time by contacting us at hello@lulurewards.co.uk. Access to the platform will continue until the end of the current billing period.';
    const terminationLine2 = 'Lulu Rewards reserves the right to suspend or terminate your account immediately and without notice if you are in material breach of these Terms, including non-payment.';
    const terminationLine3 = 'Upon termination of your account, your business configuration data and associated customer data held by Lulu Rewards will be retained for 30 days, after which it will be permanently deleted.';

    const governingLawHeading = 'Governing law';
    const governingLawLine1 = 'These Terms and Conditions shall be governed by and construed in accordance with the laws of England and Wales.';
    const governingLawLine2 = 'Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.';

    const contactHeading = 'Contact information';
    const contactLine = 'For any questions or concerns regarding these Business Terms &amp; Conditions, please contact us at <a href="mailto:hello@lulurewards.co.uk">hello@lulurewards.co.uk</a>.';

    return {
      eligibilityHeading, eligibilityLine1, eligibilityLine2, eligibilityLine3,
      servicesHeading, servicesLine1, servicesLine2, servicesLine3,
      subscriptionHeading, subscriptionLine1, subscriptionLine2, subscriptionLine3, subscriptionLine4, subscriptionLine5,
      obligationsHeading, obligationsLine1, obligationsLine2, obligationsLine3, obligationsLine4,
      ipHeading, ipLine1, ipLine2, ipLine3,
      dataHeading, dataLine1, dataLine2, dataLine3, dataLine4, dataLine5, dataLine6,
      businessPrivacyHeading, businessPrivacyLine1, businessPrivacyLine2, businessPrivacyLine3, businessPrivacyLine4, businessPrivacyLine5, businessPrivacyLine6, businessPrivacyLine7, businessPrivacyLine8,
      liabilityHeading, liabilityLine1, liabilityLine2, liabilityLine3, liabilityLine4,
      terminationHeading, terminationLine1, terminationLine2, terminationLine3,
      governingLawHeading, governingLawLine1, governingLawLine2,
      contactHeading, contactLine,
    };
  },
};
</script>

<style scoped>
.content {
  padding: 20px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: rgb(0, 0, 0);
}

.terms-title {
  font-size: 18px;
  font-weight: 700;
  color: rgb(0, 109, 62);
  margin-bottom: 8px;
}

.intro-text {
  font-size: 14px;
  color: #555;
  margin-bottom: 20px;
  line-height: 1.75;
  border-left: 3px solid rgb(0, 109, 62);
  padding-left: 12px;
}

ol,
ul {
  margin-left: 20px;
}

strong {
  font-weight: bold;
}

a {
  color: rgb(0, 109, 62);
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
